import React, { Component } from 'react';
import numeral from 'numeral';
import _ from 'lodash';
import moment from 'moment';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
import { WithStyles, withStyles } from '@mui/styles';
import { CircularProgress } from '@mui/material';

// Contexts
import { withTranslate, withTenant } from '../../core/TenantProvider';
// Components - Atoms, Organisms,Containers
import APDropDownMenu from '../../components/atoms/APDropDownMenu';
import APSection from './../../components/atoms/APSection';
import APSegmentedControl from './../../components/atoms/APSegmentedControl';
import APSeriesChart from './../../components/organisms/APSeriesChart';
import APSeriesTable from './../../components/organisms/APSeriesTable';
import APStatistic from './../../components/atoms/APStatistic';
import PDFSeriesTable from './../../components/organisms/APSeriesTable/utils/pdf';
import { APSelectDropdown } from '../../components/atoms/APSelectDropdown';
import APBackdropLoader from '../../components/atoms/APBackdropLoader';
import { APTableSkeletonLoader } from '../../components/atoms/APSkeletonLoader';
import { APTableBlankData } from '../../components/atoms/APTableBlankData';
// Files in the same folder
import ReferralsContainerStyles from './styles/ReferralsContainer.style';
import ReferralsNoDataView from './ReferralsNoDataView';
// Types
import { dropdownOptionType, LoadingEvent } from '../../core/types/GeneralTypes';
import { MonthStatementDataType } from '../../core/types/GenrateMonthStatements';
// Reducer
import { actions as ReferralsActions } from './../../redux/modules/referrals';
import { ReducerState } from './../../redux/modules';
// Utils
import { ColorScale } from './../../core/utils/Chart';
import { generate as xlsx } from './../../components/organisms/APSeriesTable/utils/xlsx';
import { TriggerGoogleAnalytics4Tag, TriggerGoogleAnalyticsTag } from '../../core/utils/GoogleAnalyticTag';
import { GoogleAnalyticTags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalyticsTags';
import { getReferralsViewType } from '../../core/utils/GetReferralsViewType';
import { GetAllAffiliateDetails } from '../../core/utils/Affiliates';
import { GoogleAnalytics4Tags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
import { SortAffiliates } from '../../core/utils/SortAffiliates';
import GetDropdownFormat from '../../core/utils/GetDropdownFormat';
import { PdfSaveAs } from '../../core/utils/PDFGenerate';
// Constants
import {
  COMMISSIONS_DETAILED_VIEW,
  COMMISSIONS_SUMMARY_VIEW,
  DEFAULT_COMMISSIONS_BREAKPOINT,
  DEFAULT_COMMISSIONS_DETAILED_BREAKPOINT,
  DEFAULT_COMMISSIONS_DETAILED_MED_BREAKPOINT,
  PDF_DOCUMENT_TYPE,
  XLSX_DOCUMENT_TYPE,
  REFERRAL_TYPE,
  REGISTRATION_TYPE,
  TRADES_TYPE,
  DEFAULT_REFERRALS_SEARCH_DETAILED_BREAKPOINT,
  FILTER_OPTIONS,
  AFFILIATES_TOTAL_GROUP_VALUE,
} from '../../core/utils/Constants/Constants';

interface ReferralsContainerProps extends WithStyles<typeof ReferralsContainerStyles> {
  details: any,
  searchDetails: any,
  getReferralsDetail: Function,
  getReferralsSummary: Function,
  getReferralsMonths: Function,
  referralsDetailReset: Function,
  referralsSearchReset: Function,
  results: any,
  monthStatements: MonthStatementDataType[],
  settings: any,
  translate: Function,
  affiliateDetails: any,
  loadingState: LoadingEvent,
  searchLoadingState: string,
  summaryLoadingState: string,
  language: any,
  forTestingCall?: boolean,
}

class ReferralsContainer extends Component<ReferralsContainerProps, {}> {
  table: any;

  state = {
    filter: REFERRAL_TYPE,
    statement: null,
    statementInputValue: '',
    referralsViewType: COMMISSIONS_SUMMARY_VIEW,
    isGroupAffiliate: false,
    allAffiliatesData: [],
    allAffiliatesNumbers: [],
    selectedAffiliate: '',
    disabled: true,
    isLoading: false,
    isSearchLoading: false
  };

  constructor(props: ReferralsContainerProps) {
    super(props);

    this.table = React.createRef();
    this.onChangeFilter = this.onChangeFilter.bind(this);
    this.onChangeStatement = this.onChangeStatement.bind(this);
    this.onChangeOfAffiliate = this.onChangeOfAffiliate.bind(this);
    this.onDownload = this.onDownload.bind(this);
    this.renderTableLoader = this.renderTableLoader.bind(this);
    this.renderFilterSection = this.renderFilterSection.bind(this);
    this.renderChartSection = this.renderChartSection.bind(this);
    this.renderSummarySection = this.renderSummarySection.bind(this);
  }

  updateDimensions = () => {
    const { affiliateDetails, getReferralsSummary, getReferralsMonths, translate, forTestingCall = false } = this.props;
    const { selectedAffiliate } = this.state;
    const {
      allAffiliatesNumbers,
      baseAffiliateNumberArray
    } = GetAllAffiliateDetails(affiliateDetails, translate, 'referrals');

    let setAffiliateNumber: string[] | [] = baseAffiliateNumberArray;

    if (selectedAffiliate === AFFILIATES_TOTAL_GROUP_VALUE || selectedAffiliate !== "") {
      setAffiliateNumber = selectedAffiliate === AFFILIATES_TOTAL_GROUP_VALUE ? allAffiliatesNumbers : [selectedAffiliate];
    }

    const referralSummaryAndMonthsApiPayload: string[] = setAffiliateNumber.length > 0 ? setAffiliateNumber : baseAffiliateNumberArray;

    !forTestingCall && getReferralsSummary(referralSummaryAndMonthsApiPayload, translate);

    !forTestingCall && getReferralsMonths(referralSummaryAndMonthsApiPayload, translate).then((response: MonthStatementDataType[]) => {
      if (response?.length > 0) {
        this.refresh(response[0]);
      }
    }).catch((error: any) => console.error('Error:', error));
  }

  onChangeFilter(filter: any) {
    this.setState({
      filter
    });

    if (GoogleAnalyticTags.referrals.click.filter[filter]) {
      TriggerGoogleAnalyticsTag(
        GoogleAnalyticTags.eventAction.click,
        GoogleAnalyticTags.referrals.click.filter[filter]
      );
      TriggerGoogleAnalytics4Tag(
        GoogleAnalytics4Tags.referrals.name,
        GoogleAnalytics4Tags.eventAction.click,
        GoogleAnalytics4Tags.referrals.click.filter[filter]
      );
    }
  }

  onChangeStatement(evt: any) {

    const {
      target: {
        value
      }
    } = evt;

    TriggerGoogleAnalyticsTag(
      GoogleAnalyticTags.eventAction.select,
      GoogleAnalyticTags.referrals.select.filterMonth,
      { filterMonth: value }
    );

    TriggerGoogleAnalytics4Tag(
      GoogleAnalytics4Tags.referrals.name,
      GoogleAnalytics4Tags.eventAction.select,
      GoogleAnalytics4Tags.referrals.select.filterMonth,
      { filterMonth: value }
    );

    if (value && value.length > 0) {
      const { monthStatements } = this.props;
      const selectedStatement = monthStatements.filter((statement) => {
        return statement.value.toString() === value;
      });
      this.refresh(selectedStatement[0], true);
    }
  }

  onChangeOfAffiliate(evt: any) {
    const {
      target: {
        value
      }
    } = evt;

    const {
      getReferralsSummary,
      getReferralsMonths,
      referralsDetailReset,
      translate
    } = this.props;

    let affiliateNumbers;

    if (value !== AFFILIATES_TOTAL_GROUP_VALUE) {
      affiliateNumbers = [value];
    } else {
      affiliateNumbers = this.state.allAffiliatesNumbers
    }

    this.setState({
      selectedAffiliate: value
    }, () => {
      getReferralsSummary(affiliateNumbers, translate)
      getReferralsMonths(affiliateNumbers, translate).then((response: MonthStatementDataType[]) => {
        if (response?.length > 0) {
          this.refresh(response[0]);
        } else {
          referralsDetailReset();
        }
      }).catch((error: any) => console.error('Error:', error));
    });

  }

  getGroupsByType(details, translate) {
    return {
      REFERRAL_TYPE: {
        series: details.events.filter((item: any) => item.event.eventType === REFERRAL_TYPE),
        title: translate && translate('referrals.filters.referrals.label')
      },
      REGISTRATION_TYPE: {
        series: details.events.filter((item: any) => item.event.eventType === REGISTRATION_TYPE),
        title: translate && translate('referrals.filters.registrations.label')
      },
      TRADES_TYPE: {
        series: details.events.filter((item: any) => item.event.eventType === TRADES_TYPE),
        title: translate && translate('referrals.filters.trades.label')
      }
    };
  }

  onDownload = async (type: string) => {
    const {
      details,
      translate,
      affiliateDetails,
      settings
    } = this.props;

    this.setState({ isLoading: true });

    TriggerGoogleAnalyticsTag(
      GoogleAnalyticTags.eventAction.click,
      GoogleAnalyticTags.referrals.click.downloadLink
    );

    TriggerGoogleAnalytics4Tag(
      GoogleAnalytics4Tags.referrals.name,
      GoogleAnalytics4Tags.eventAction.click,
      GoogleAnalytics4Tags.referrals.click.downloadLink
    );

    let groups: any;
    if (details && details.events) {
      groups = this.getGroupsByType(details, translate);
    }
    let statementMonth: string = '';
    if (this.state.statement) {
      const statement: any = this.state.statement;
      statementMonth = `${statement?.statementMonthString} ${statement?.statementYear}`;
    }

    if (type === XLSX_DOCUMENT_TYPE) {

      let affiliateName: string = affiliateDetails?.affiliate?.company_name;
      xlsx(groups, 'Referrals Statement.xlsx', true, translate, affiliateName, 'referralsView', '', statementMonth);
      this.setState({ isLoading: false });
    }
    if (type === PDF_DOCUMENT_TYPE) {
      const PdfDocument = this.PDF({ settings, translate, affiliateDetails, groups, statementMonth });
      if (!PdfDocument) { return; }
      const blob = pdf((
        PdfDocument
      ));
      if (blob) {
        blob.updateContainer(PdfDocument);
        setTimeout(async () => {
          const result = await blob.toBlob();
          const fileName = "referrals-pdf.pdf";
          this.setState({ isLoading: false });
          PdfSaveAs(result, fileName);
        }, 1000);
      }

    }
  }


  PDF = ({ settings, translate, affiliateDetails, groups, statementMonth }) => (
    <PDFSeriesTable
      org={settings.ORG_CODE}
      logo={settings.LOGO_PNG}
      groups={groups}
      title={translate('referrals.section.referrals.title')}
      translate={translate}
      affiliateData={affiliateDetails?.affiliate}
      statementMonth={statementMonth}
    />
  );

  // Helper Function: Get Affiliate Numbers
  getAffiliateNumbers(isGroupAffiliate: boolean, allAffiliatesNumbers: string[], baseAffiliateNumberArray: string[]) {
    if (this.state.selectedAffiliate === AFFILIATES_TOTAL_GROUP_VALUE) {
      return allAffiliatesNumbers;
    }
    return this.state.selectedAffiliate ? [this.state.selectedAffiliate] : (isGroupAffiliate ? baseAffiliateNumberArray : allAffiliatesNumbers);
  }

  updateFilterBasedOnCounts(response: any) {
    const groups: any = response.events ? this.getGroupsByType(response, false) : {};

    const referralsCount = groups.referral?.series.length || 0;
    const registrationsCount = groups.registration?.series.length || 0;
    const tradesCount = groups.trades?.series.length || 0;

    let filterType;
    if (referralsCount > 0) {
      filterType = REFERRAL_TYPE;
    } else if (registrationsCount > 0) {
      filterType = REGISTRATION_TYPE;
    } else if (tradesCount > 0) {
      filterType = TRADES_TYPE;
    }

    if (filterType) {
      this.setState({ filter: filterType });
    }
  }

  refresh(statement: any, isChanged?: boolean) {
    const {
      getReferralsDetail,
      affiliateDetails,
      referralsDetailReset,
      translate
    } = this.props;

    // Enable/Disable state based on statement
    this.setState({
      disabled: !statement,
      statement,
      statementInputValue: statement?.value
    });

    if (!statement) {
      referralsDetailReset();
      return;
    }

    const referralsViewType = getReferralsViewType(affiliateDetails);

    const {
      isGroupAffiliate,
      allAffiliatesData,
      allAffiliatesNumbers,
      baseAffiliateNumberArray
    } = GetAllAffiliateDetails(affiliateDetails, translate, 'referrals');

    const currentAffiliateNumbers: Array<string> = this.getAffiliateNumbers(isGroupAffiliate, allAffiliatesNumbers, baseAffiliateNumberArray);

    this.setState({
      statement,
      statementInputValue: statement?.value,
      referralsViewType,
      isGroupAffiliate,
      allAffiliatesData,
      allAffiliatesNumbers
    }, () => {

      getReferralsDetail(statement,
        referralsViewType,
        currentAffiliateNumbers,
        affiliateDetails?.affiliate?.commission_view,
        translate
      ).then((response: any) => {
        if (response.events && isChanged) {
          this.updateFilterBasedOnCounts(response);
        }
      });

    });
  }

  componentDidMount() {
    const {
      affiliateDetails,
      translate,
      referralsSearchReset
    } = this.props;

    const {
      isGroupAffiliate,
      allAffiliatesData,
      allAffiliatesNumbers
    } = GetAllAffiliateDetails(affiliateDetails, translate, 'referrals');

    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.updateDimensions();
      }, 500);
    });

    this.setState({
      isGroupAffiliate,
      allAffiliatesData,
      allAffiliatesNumbers,
      ...(isGroupAffiliate ? { selectedAffiliate: AFFILIATES_TOTAL_GROUP_VALUE } : {}),
    }, () => {
      this.updateDimensions();
    });
    referralsSearchReset();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate(prevProps: any, prevState: any, snapshot?: any): void {
    const {
      affiliateDetails,
      translate
    } = this.props;

    const groupAffiliates = affiliateDetails?.GroupData?.length > 0 ? affiliateDetails.GroupData : [],
      isGroupAffiliate = groupAffiliates.length > 0;

    if (affiliateDetails?.affiliate?.commission_view &&
      (affiliateDetails?.affiliate?.commission_view !== this.state.referralsViewType ||
        this.state.isGroupAffiliate !== isGroupAffiliate)) {

      const allAffiliatesData = isGroupAffiliate ? getAllAffiliatesData(affiliateDetails, groupAffiliates, translate) : [];

      const allAffiliatesNumbers = allAffiliatesData.reduce(function (affiliateIds, affiliate) {
        if (affiliate.affiliateNumber !== AFFILIATES_TOTAL_GROUP_VALUE) { // this serves as our `filter`
          affiliateIds.push(affiliate.affiliateNumber);
        }
        return affiliateIds;
      }, []);

      if (this.state.isGroupAffiliate !== isGroupAffiliate) {
        this.setState({
          isGroupAffiliate,
          allAffiliatesData,
          allAffiliatesNumbers,
          ...(isGroupAffiliate ? { selectedAffiliate: AFFILIATES_TOTAL_GROUP_VALUE } : { selectedAffiliate: '' }),
        })
      }
    }
  }

  getFilteredSeries(events: any[], filter: string | null) {
    return filter ? events?.filter((item) => item.event.eventType === filter) : events;
  }

  getStatementsDropdown(statements: any[]) {
    return statements?.map((statement) => ({
      label: moment(String(statement.value)).format('MMMM YYYY').replace(/^./, (str) => str.toUpperCase()),
      value: statement.value
    })) || [];
  }

  getSortedAffiliatesData() {
    if (!this.state.isGroupAffiliate || !this.state.allAffiliatesData) return null;
    const formattedData = GetDropdownFormat(this.state.allAffiliatesData);
    return formattedData.length > 1 ? SortAffiliates(formattedData) : formattedData;
  }

  getBreakpoint(searchState: string, filter: string, referralsViewType: string) {
    return searchState === 'LOADED'
      ? DEFAULT_REFERRALS_SEARCH_DETAILED_BREAKPOINT
      : getBreakpointByReferralView(filter, referralsViewType);
  }

  // Helper Functions
  renderTableLoader = (searchLoadingState, breakpoint, referralsSearchSeries, referralSearchString) => {
    const { classes } = this.props;

    if (searchLoadingState === 'LOADING') {
      return <APTableSkeletonLoader module={'referralsSearch'} breakPoint={breakpoint} skeletonRows={10} />;
    }
    if (searchLoadingState === 'LOADED') {
      return referralsSearchSeries?.length > 0 ? (
        <div className={classes.accountContainer}>
          <APSeriesTable
            hideEvent
            pagination={10}
            series={referralsSearchSeries}
            breakPoint={breakpoint}
            referralsSearchEnable={true}
            referralSearchString={referralSearchString}
          />
        </div>
      ) : (
        <APTableBlankData referralSearchString={referralSearchString} />
      );
    }
    return null;
  }

  renderSummarySection = (allAffiliatesData, results, translate) => {
    const { classes } = this.props;

    const color = ColorScale();

    return (
      <APSection
        className={classes.accountSectionSummary}
        title={translate('referrals.section.referrals.title')}
        dropdownData={allAffiliatesData}
        onDropdownChange={this.onChangeOfAffiliate}
        initialDropdownValue={this.state.selectedAffiliate || AFFILIATES_TOTAL_GROUP_VALUE}
      >
        {results.summaries && (
          <div className={classes.statistics}>
            {Object.keys(results.summaries).map((summary) => (
              <APStatistic
                color={color(summary)}
                name={translate(`referrals.statistics.${summary}.name`)}
                summary={translate(`referrals.statistics.${summary}.summary`)}
                value={numeral(results.summaries[summary]).format('0,000')}
              />
            ))}
          </div>
        )}
      </APSection>
    );
  }

  renderChartSection = (summaryLoadingState, results) => {
    const { classes } = this.props;

    return (
      <APSection>
        {summaryLoadingState === 'LOADING' ? (
          <div className={classes.graphLoader}>
            <CircularProgress className={classes.graphLoaderSpinner} />
          </div>
        ) : (
          <APSeriesChart xSuffixThisYear series={results.series} height={300} width="100%" />
        )}
      </APSection>
    );
  }

  renderFilterSection = (statements, statementInputValue, disableDownload) => {
    const { classes, translate } = this.props;

    return (
      <div className={classes.filterContainer}>
        <APSegmentedControl
          onChange={this.onChangeFilter}
          options={FILTER_OPTIONS.map((singleFilter) => ({
            ...singleFilter,
            label: translate(singleFilter.label),
          }))}
          selection={this.state.filter}
        />
        <div className={classes.filterRight}>
          <APSelectDropdown
            value={statementInputValue}
            onChange={this.onChangeStatement}
            label={translate('referrals.section.month.dropdown.label')}
            data={statements}
            disabled={this.state.disabled}
            name={'Statement Months'}
          />
          <APDropDownMenu
            disabled={this.state.disabled || disableDownload}
            className={classes.dropDownMenu}
            label={"Download"}
            onChange={this.onDownload}
            options={[
              { label: 'PDF', value: 'pdf' },
              { label: "XLSX", value: 'xlsx' }
            ]}
            hasDownloadIcon={true}
          />
        </div>
      </div>
    );
  }

  renderTable = (loadingState, series, breakpoint) => {
    const { classes } = this.props;
    const { filter } = this.state;

    if (loadingState === 'LOADING') {
      return (
        <div className={classes.graphLoader}>
          <CircularProgress className={classes.graphLoaderSpinner} />
        </div>
      );
    }
    return series?.length > 0 ? (
      <APSeriesTable hideEvent pagination={10} series={series} breakPoint={breakpoint} />
    ) : (
      <ReferralsNoDataView filterType={filter} />
    );
  }

  render() {
    const {
      classes,
      details,
      searchDetails,
      results,
      translate,
      loadingState,
      searchLoadingState,
      summaryLoadingState,
      monthStatements
    } = this.props;

    if (!results) return null;

    // Prepare data for download
    const downloadData = details?.events?.length > 0 ? details.events : [];
    const disableDownload = downloadData.length === 0;

    // Determine referrals view type
    const referralsViewType = this.state.referralsViewType;

    // Get filtered series
    const series = this.getFilteredSeries(details?.events, this.state.filter);

    // Get referrals search series
    const referralsSearchSeries = searchDetails?.events;
    const referralSearchString = searchDetails?.searchValue;

    // Process month statements
    const statements: dropdownOptionType[] | [] = this.getStatementsDropdown(monthStatements);


    let statementInputValue: any = this.state.statementInputValue;

    // Process affiliate data
    const allAffiliatesData: any = this.getSortedAffiliatesData();

    // Determine breakpoint
    const breakpoint = this.getBreakpoint(searchLoadingState, this.state.filter, referralsViewType);

    const { isLoading } = this.state;

    return (
      <div>
        {(isLoading) ? <APBackdropLoader open={true} /> : ''}

        {this.renderTableLoader(searchLoadingState, breakpoint, referralsSearchSeries, referralSearchString)}

        {searchLoadingState !== 'LOADING' && !searchDetails && (
          <div className={classes.accountContainer} ref={this.table}>
            <div className={classes.accountContainerContent}>
              {this.renderSummarySection(allAffiliatesData, results, translate)}
              {this.renderChartSection(summaryLoadingState, results)}
              {this.renderFilterSection(statements, statementInputValue, disableDownload)}
              {this.renderTable(loadingState, series, breakpoint)}
            </div>
          </div>
        )}
      </div>
    );
  }
}

const getAllAffiliatesData = (affiliateDetails, groupAffiliates, translate) => {
  const allAffiliatesData = groupAffiliates.map((affiliate) => (
    {
      affiliateNumber: affiliate.affNumbers,
      affiliateName: affiliate.name,
      subAffiliate: affiliate.subAffiliate
    }
  ));

  return [
    {
      affiliateNumber: affiliateDetails.affiliate.affiliate_Number,
      affiliateName: affiliateDetails.affiliate.company_name,
      subAffiliate: []
    },
    {
      affiliateNumber: AFFILIATES_TOTAL_GROUP_VALUE,
      affiliateName: translate('referrals.group.affiliate.label'),//'Total Group'
      subAffiliate: []
    },
    ...allAffiliatesData
  ];
};

const getBreakpointByReferralView = (filter: string, referralsViewType: string) => {
  switch (filter) {
    case REFERRAL_TYPE:
      return (referralsViewType === COMMISSIONS_DETAILED_VIEW) ? DEFAULT_COMMISSIONS_DETAILED_BREAKPOINT : DEFAULT_COMMISSIONS_BREAKPOINT;

    case REGISTRATION_TYPE:
      return (referralsViewType === COMMISSIONS_DETAILED_VIEW) ? DEFAULT_COMMISSIONS_DETAILED_MED_BREAKPOINT : DEFAULT_COMMISSIONS_BREAKPOINT;

    case TRADES_TYPE:
      return (referralsViewType === COMMISSIONS_DETAILED_VIEW) ? DEFAULT_COMMISSIONS_DETAILED_BREAKPOINT : DEFAULT_COMMISSIONS_BREAKPOINT;

    default:
      return DEFAULT_COMMISSIONS_DETAILED_BREAKPOINT;
  }
};

const mapStateToProps = (state: ReducerState) => ({
  details: state.referrals.details,
  searchDetails: state.referrals.searchDetails,
  results: state.referrals.results,
  affiliateDetails: state.user.affiliateDetails,
  loadingState: state.referrals.loadingState,
  searchLoadingState: state.referrals.searchLoadingState,
  summaryLoadingState: state.referrals.summaryLoadingState,
  monthStatements: state.referrals.monthStatements
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
  getReferralsDetail: ReferralsActions.getReferralsDetail,
  getReferralsSummary: ReferralsActions.getReferralsSummary,
  getReferralsMonths: ReferralsActions.getReferralsMonths,
  referralsDetailReset: ReferralsActions.referralsDetailReset,
  referralsSearchReset: ReferralsActions.referralsSearchReset,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
  withTenant(
    withTranslate(
      withStyles(ReferralsContainerStyles)(ReferralsContainer)
    )
  )
);
